import React, { useEffect, useState, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './SuccessPage.css';

const SuccessPage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [subscriptionActive, setSubscriptionActive] = useState(false);
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const [sessionId, setSessionId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [userData, setUserData] = useState(null);

    // Parse URL parameters immediately on component mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const userId = params.get('user_id');
        
        if (sessionId) {
            setSessionId(sessionId);
            // Store in sessionStorage as fallback
            sessionStorage.setItem('stripeSessionId', sessionId);
        }
        
        if (userId) {
            setUserId(userId);
            // Store in sessionStorage as fallback
            sessionStorage.setItem('stripeUserId', userId);
            
            // Try direct Firestore check with user ID from URL
            fetchUserDataDirectly(userId);
        }
    }, [location]);

    // Function to fetch user data directly from Firestore using URL parameter
    const fetchUserDataDirectly = async (uid) => {
        if (!uid) return;
        
        try {
            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
                const data = userSnap.data();
                setUserData(data);
                
                // Check for new role-based system first, then fallback to legacy boolean
                const isProUser = data.role === 'pro_user' || data.isPro === true;
                setSubscriptionActive(isProUser);
                setLoading(false);
            }
        } catch (err) {
            console.error("Error in direct Firestore fetch:", err);
        }
    };

    // Set up polling for auth state
    useEffect(() => {
        // If we already have user data from direct Firestore query, don't need auth
        if (userData && (userData.role === 'pro_user' || userData.isPro)) {
            setLoading(false);
            return;
        }
        
        // If no user after 15 seconds (5 attempts x 3 seconds), try direct method
        if (attempts >= 5 && !user) {
            const storedUserId = sessionStorage.getItem('stripeUserId');
            if (storedUserId && !userData) {
                fetchUserDataDirectly(storedUserId);
            } else {
                setError("Authentication issue detected. Please try refreshing the page.");
                setLoading(false);
            }
            return;
        }

        // If no user yet, but we haven't exceeded attempts, try again
        if (!user && attempts < 5) {
            const timer = setTimeout(() => {
                setAttempts(prev => prev + 1);
            }, 3000);
            return () => clearTimeout(timer);
        }

        // If we have the user, check subscription
        if (user?.uid) {
            checkSubscriptionStatus();
        }
    }, [user, attempts, userData]);
    
    const checkSubscriptionStatus = async () => {
        try {
            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
                const userData = userSnap.data();
                
                // Check for new role-based system first, then fallback to legacy boolean
                const isProUser = userData.role === 'pro_user' || userData.isPro === true;
                setSubscriptionActive(isProUser);
                setUserData(userData);
            } else {
                console.error("User document not found");
                setError("We couldn't verify your subscription. Please contact support.");
            }
        } catch (err) {
            console.error("Error checking subscription:", err);
            setError("An error occurred while verifying your subscription.");
        } finally {
            setLoading(false);
        }
    };

    // Show manual redirect button if loading takes too long
    const handleManualReturn = () => {
        navigate('/auth-home');
    };

    // Add this useEffect to automatically show the success container with animation
    useEffect(() => {
        if (!loading && !error) {
            const container = document.querySelector('.success-container');
            if (container) {
                setTimeout(() => {
                    container.classList.add('show');
                }, 100);
            }
        }
    }, [loading, error]);

    // Network status check (no logging needed)

    if (loading) {
        return (
            <div className="success-page">
                <div className="success-container loading-container show">
                    <h2>Processing your subscription...</h2>
                    <div className="loading-spinner"></div>
                    <p>This may take a few moments. Please don't close this page.</p>
                    {attempts >= 3 && (
                        <button onClick={handleManualReturn} className="manual-return-button">
                            Manually Return to Dashboard
                        </button>
                    )}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="success-page">
                <div className="success-container error">
                    <h2>Something went wrong</h2>
                    <p>{error}</p>
                    <p>If your payment was successful, your account will be updated shortly.</p>
                    <div className="error-details">
                        <p>Session ID: {sessionId || 'Not found'}</p>
                        <p>User ID: {userId || (user ? user.uid : 'Not found')}</p>
                    </div>
                    <Link to="/auth-home" className="return-button">
                        Return to Dashboard
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="success-page">
            <div className="success-container">
                <div className="success-icon">
                    <svg viewBox="0 0 24 24" width="100" height="100">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                </div>
                <h2>Subscription Successful!</h2>
                <div className="success-message">
                    <p>Thank you for subscribing to Dannytopia Pro!</p>
                    {subscriptionActive ? (
                        <p>Your pro features are now active and you have full access to all premium content.</p>
                    ) : (
                        <p className="warning">Your account is being updated. If pro features are not available in a few minutes, please contact support.</p>
                    )}
                </div>
                <div className="benefits-list">
                    <h3>What's included in your subscription:</h3>
                    <ul>
                        <li>✨ Access to all Pro Galleries</li>
                        <li>✨ New content added twice a month</li>
                        <li>✨ Direct messaging with the creator</li>
                        <li>✨ Early access to new features</li>
                        <li>✨ Access to partially customized photo requests</li>
                        <li>✨ Access to Purchase Videos</li>
                    </ul>
                </div>
                <Link to="/auth-home" className="return-button">
                    Return to Dashboard
                </Link>
            </div>
        </div>
    );
};

export default SuccessPage;
